import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AiDraftSourcesModel} from '../../../../../../../libs/shared/core/src/models/api/aiDraftSourcesModel';
import {InboxModel} from '../../../../../../../libs/shared/core/src/models/api/inboxModel';
import {DraftMessageModel} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  buildTeammateDisplayName,
  buildTeammateShortDisplayName,
  isRuleTeammate,
} from '../../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  InterfaceEventsEnum,
  ReplyLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  ComposerPanelId,
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {ThreeDots} from '../../../../../../../libs/shared/ui/src/components/composer/threeDots';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {OkCancelDialog} from '../../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {DropdownHeader} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {SimpleDropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../../srcCommon/browser/openInBrowser';
import {InboxesFormatter} from '../../../../../common/formatters/inboxesFormatter';
import {TeammatesFormatter} from '../../../../../common/formatters/teammatesFormatter';
import {MessageBanner} from './messageBanner';
import {MessageBannerAction} from './messageBannerAction';
import {MessageBannerActionGroup, MessageBannerActionGroupProps} from './messageBannerActionGroup';
import {MessageBannerSubtitle} from './messageBannerSubtitle';
import {MessageBannerTitle} from './messageBannerTitle';
import {PrivateReplyTooltipCoordinatorContainer} from './privateReplyTooltipCoordinatorContainer';

/*
 * Props.
 */

interface SharedDraftMessageBannerProps extends MessageBannerActionGroupProps {
  message: DraftMessageModel;
  author: TeammateModel;
  lastUpdatedAt?: number;
  isAuthorReplying?: boolean;
  hasChannelToEdit: boolean;
  onStealDraftClick?: () => void;
  isRounded: boolean;
  isComposerPopup: boolean;
  teamInboxes: ReadonlyArray<InboxModel>;
  participants: ReadonlyArray<TeammateModel>;
  canReplyToMessage: boolean;
  panelId: ComposerPanelId | ViewerPanelId | MessagePanelId | EmlPanelId;
  userId: number;
  hasAutoShareDrafts: boolean;
}

/*
 * Style.
 */

const StyledTitleDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledPrecogDiv = styled.div`
  margin-left: 6px;
  flex-shrink: 0;
`;

const StyledBannerActionMetaDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 7px;
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade80};
  ${ellipsis()};
`;

const StyledTakeOverActionDiv = styled.div`
  flex-shrink: 0;
`;

const AiGeneratedDiv = styled.div`
  display: flex;
  flex-direction: row;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  text-transform: uppercase;
  // Bright pink to make sure we change it before GA
  color: ${(p) => p.theme.palette.pink.shade40};
  gap: 5px;
  align-items: center;
  padding: 4px 12px;
  border-radius: 10px;

  &:hover {
    background-color: ${(p) => p.theme.palette.pink.shade20};
  }
`;

/*
 * Component.
 */

export const SharedDraftMessageBanner: FC<SharedDraftMessageBannerProps> = (props) => {
  const {author, isAuthorReplying, onStealDraftClick} = props;
  const [isStealConfirmationOpen, toggleIsStealConfirmationOpen] = useToggle();

  /*
   * Event handlers.
   */

  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.CONVERSATION_SHARED_DRAFT_VIEW, {
      message_id: props.message.id,
      conversation_id: props.message.conversationId,
      is_draft_editor: !onStealDraftClick,
    });
  });

  const onStealButtonClick = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.CLICK_EDIT_SHARED_DRAFT, {
      message_id: props.message.id,
      conversation_id: props.message.conversationId,
    });

    // If the draft is not being actively edited, let's skip the check and let the user steal the draft.
    if (!isAuthorReplying && onStealDraftClick) {
      onStealDraftClick();
      return;
    }

    toggleIsStealConfirmationOpen();
  }, [
    isAuthorReplying,
    onStealDraftClick,
    props.message.conversationId,
    props.message.id,
    toggleIsStealConfirmationOpen,
  ]);

  const onStealConfirmClick = useCallback(() => {
    toggleIsStealConfirmationOpen();

    if (onStealDraftClick) {
      onStealDraftClick();
    }
  }, [toggleIsStealConfirmationOpen, onStealDraftClick]);

  /*
   * Render.
   */

  return (
    <>
      {<SharedDraftBanner {...props} onStealButtonClick={onStealButtonClick} />}
      {isStealConfirmationOpen && (
        <StealDraftConfirmation
          author={author}
          onConfirmClick={onStealConfirmClick}
          onRequestClose={toggleIsStealConfirmationOpen}
        />
      )}
    </>
  );
};

/*
 * Banner.
 */

interface SharedDraftBannerProps extends SharedDraftMessageBannerProps {
  onStealButtonClick: () => void;
}

const SharedDraftBanner: FC<SharedDraftBannerProps> = (props) => {
  const theme = useUserTheme();
  const {canReplyToMessage, onStealDraftClick, isAuthorReplying, hasAutoShareDrafts} = props;

  // Show tooltip when the message is a reply, and isAuthorReplying is true or undefined.
  const shouldShowPrivateReplyTooltip =
    canReplyToMessage && isAuthorReplying !== false && !hasAutoShareDrafts;

  return (
    <MessageBanner
      {...props}
      color={hasAutoShareDrafts ? theme.alphas.gray20 : theme.interactiveText}
      iconName={hasAutoShareDrafts ? 'sharedFilledSmall' : 'draftFilledSmall'}
      iconColor={(hasAutoShareDrafts && theme.greys.shade80) || undefined}
      isRounded={props.isRounded}
    >
      <MessageBannerTitle color={(hasAutoShareDrafts && theme.greys.shade80) || undefined}>
        <StyledTitleDiv>
          <Trans id="shareddraftbanner-title">Shared draft</Trans>
        </StyledTitleDiv>
      </MessageBannerTitle>
      {renderBannerSubtitle(props)}
      {shouldShowPrivateReplyTooltip && (
        <PrivateReplyTooltipCoordinatorContainer
          location={ReplyLocationsEnum.SHARED_DRAFT_BANNER_PRIVATE_REPLY_TOOLTIP}
          message={props.message}
          panelId={props.panelId}
          isComposerPopup={props.isComposerPopup}
          iconColor={(hasAutoShareDrafts && theme.greys.shade80) || undefined}
        />
      )}
      <MessageBannerActionGroup
        onForcePopout={props.onForcePopout}
        onRequestClose={props.onRequestClose}
        color={hasAutoShareDrafts ? theme.interactiveText : undefined}
        useDarkHover={hasAutoShareDrafts}
      >
        {hasAutoShareDrafts && renderBannerActionMeta(props)}
        {onStealDraftClick && (
          <StyledTakeOverActionDiv>
            <TooltipCoordinator render={() => maybeRenderNoEditTooltip(props.hasChannelToEdit)}>
              <MessageBannerAction
                onClick={props.onStealButtonClick}
                isDisabled={!props.hasChannelToEdit}
                textColor={(hasAutoShareDrafts && theme.interactiveText) || undefined}
                useDarkHover={hasAutoShareDrafts}
              >
                {hasAutoShareDrafts ? (
                  <Trans id="shareddraftmessage-takeover">Take over</Trans>
                ) : (
                  <Trans id="shareddraft-edit">Edit</Trans>
                )}
              </MessageBannerAction>
            </TooltipCoordinator>
          </StyledTakeOverActionDiv>
        )}
      </MessageBannerActionGroup>
    </MessageBanner>
  );
};

function renderBannerActionMeta(props: SharedDraftBannerProps) {
  const {author, message, isAuthorReplying, lastUpdatedAt, hasAutoShareDrafts} = props;
  const {aiSources} = message;

  if (!author || !lastUpdatedAt || !hasAutoShareDrafts) {
    return null;
  }

  const baseTeammatePart = (
    <>
      <TeammateAvatar teammate={author} size={VisualSizesEnum.SMALL} />
      {buildTeammateShortDisplayName(author)}
    </>
  );

  if (isAuthorReplying) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledBannerActionMetaDiv>
            <Trans id="shareddraftbanner-authorisediting">
              {baseTeammatePart} is editing
              <ThreeDots color={theme.greys.shade80} size={VisualSizesEnum.SMALL} />
            </Trans>
          </StyledBannerActionMetaDiv>
        )}
      </UserThemeConsumer>
    );
  }

  if (isRuleTeammate(author) && aiSources) {
    return <StyledBannerActionMetaDiv>{renderAiGenerated(aiSources)}</StyledBannerActionMetaDiv>;
  }

  return (
    <StyledBannerActionMetaDiv>
      {baseTeammatePart} {renderEdited(lastUpdatedAt)}
    </StyledBannerActionMetaDiv>
  );
}

function renderAiGenerated(aiSources: AiDraftSourcesModel) {
  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <AiGeneratedDiv>
          <Icon name="magicWandFilledSmall" />
          <Trans id="shareddraftbanner-ai-generated">AI generated</Trans>
        </AiGeneratedDiv>
      )}
      renderModal={(popoverProps) => renderAiSourcesDropdown(popoverProps, aiSources)}
    />
  );
}

function renderAiSourcesDropdown(popoverProps: PopoverTransferProps, aiSources: AiDraftSourcesModel) {
  return (
    <SimpleDropdown {...popoverProps}>
      <DropdownHeader title={<Trans id="shareddraftbanner-ai-sources-kb">Based on these articles</Trans>} />
      {aiSources.links.map(({name, href}) => (
        <SimpleDropdownItem key={href} onSelect={() => openInBrowser(href)}>
          {name}
        </SimpleDropdownItem>
      ))}
    </SimpleDropdown>
  );
}

function renderEdited(lastUpdatedAt: number) {
  const minutesAgo = Math.floor((Date.now() - lastUpdatedAt) / 60000);
  if (minutesAgo === 0) {
    return <Trans id="shareddraft-updated-now">edited now</Trans>;
  }

  const relativeUpdatedAt = (
    <RelativeDateTimeFormatter value={lastUpdatedAt} format={RelativeDateTimeFormatsEnum.SHORT} />
  );

  return <Trans id="shareddraft-updated-minutesago">edited {relativeUpdatedAt} ago</Trans>;
}

function renderBannerSubtitle(props: SharedDraftBannerProps) {
  const {author, isAuthorReplying, lastUpdatedAt, hasAutoShareDrafts} = props;

  if (hasAutoShareDrafts) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <MessageBannerSubtitle color={theme.greys.shade80}>
            {renderVisibilityText(props)}
          </MessageBannerSubtitle>
        )}
      </UserThemeConsumer>
    );
  }

  // If the author is editing, show the precog.
  if (isAuthorReplying) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <MessageBannerSubtitle>
            <StyledTitleDiv>
              <Trans id="shareddraft-title-precog">{buildTeammateDisplayName(author)} is editing</Trans>
              <StyledPrecogDiv>
                <ThreeDots color={theme.alphas.white50} size={VisualSizesEnum.SMALL} />
              </StyledPrecogDiv>
            </StyledTitleDiv>
          </MessageBannerSubtitle>
        )}
      </UserThemeConsumer>
    );
  }

  // If the draft has been updated, show when.
  if (lastUpdatedAt) {
    return maybeRenderBannerLastEdited(props);
  }

  // Show the visibility text.
  return <MessageBannerSubtitle>{renderVisibilityText(props)}</MessageBannerSubtitle>;
}

function maybeRenderBannerLastEdited(props: SharedDraftBannerProps) {
  const {lastUpdatedAt, isAuthorReplying} = props;
  if (!lastUpdatedAt || isAuthorReplying) {
    return null;
  }

  const relativeUpdatedAt = (
    <RelativeDateTimeFormatter value={lastUpdatedAt} format={RelativeDateTimeFormatsEnum.SHORT} />
  );

  const minutesAgo = Math.floor((Date.now() - lastUpdatedAt) / 60000);
  if (minutesAgo === 0) {
    return (
      <MessageBannerSubtitle>
        <Trans id="shareddraft-lastupdated-now">Last edited now</Trans>
      </MessageBannerSubtitle>
    );
  }

  return (
    <MessageBannerSubtitle>
      <Trans id="shareddraft-lastupdated-minutesago">Last edited {relativeUpdatedAt} ago</Trans>
    </MessageBannerSubtitle>
  );
}

/** If the button is disabled, add a tooltip to explain why. */
function maybeRenderNoEditTooltip(hasChannelToEdit: boolean | undefined) {
  if (hasChannelToEdit) {
    return null;
  }

  return <Trans id="shareddraft-tooltip-permission">You do not have permission to edit this draft.</Trans>;
}

/*
 * Confirmation dialog.
 */

interface StealDraftConfirmationProps {
  author: TeammateModel;
  onConfirmClick: () => void;
  onRequestClose: () => void;
}

const StealDraftConfirmation: FC<StealDraftConfirmationProps> = (props) => {
  const {author, onConfirmClick, onRequestClose} = props;
  const authorName = buildTeammateDisplayName(author);
  return (
    <OkCancelDialog
      title={<Trans id="shareddraft-steal-title">{authorName} is editing this draft</Trans>}
      okButtonLabel={<Trans id="shareddraft-steal-ok">Edit</Trans>}
      onOkClick={onConfirmClick}
      onRequestClose={onRequestClose}
    >
      <Trans id="shareddraft-steal-content">
        <strong>{authorName}</strong> is currently editing this draft.
        <br />
        If you take over this draft, they may be interrupted.
      </Trans>
    </OkCancelDialog>
  );
};

/**
 * If the conversation is in a team inbox or has multiple participants, this clarifies such.
 * Otherwise, null is returned.
 */
function renderVisibilityText(props: SharedDraftBannerProps) {
  const {teamInboxes, participants, userId, hasAutoShareDrafts} = props;

  if (teamInboxes.length > 0) {
    const formattedInboxes = (
      <InboxesFormatter inboxes={teamInboxes} limit={2} renderInboxName={(name) => <strong>{name}</strong>} />
    );

    if (hasAutoShareDrafts) {
      return <Trans id="shareddraftbanner-visibilitytext">Editable by teammates in {formattedInboxes}</Trans>;
    }

    return (
      <Trans id="shareddraftbanner-visibility-inboxes">Editable by teammates in {formattedInboxes}</Trans>
    );
  }

  // Even if the conversation has only one participant, use this when a delegation has authored a draft.
  if (hasAutoShareDrafts) {
    return <Trans id="shareddraftbanner-visibilitytextall">All participants can edit</Trans>;
  }

  if (participants.length > 1) {
    const formattedTeammates = (
      <TeammatesFormatter
        teammates={participants}
        userId={userId}
        renderTeammateName={(name) => <strong>{name}</strong>}
        limit={3}
      />
    );
    return (
      <Trans id="shareddraftbanner-visibility-teammates">
        This message is visible to {formattedTeammates}
      </Trans>
    );
  }

  return null;
}
